import React, { useState } from 'react';
import { Mail, Check, Copy, Shield, Eye, ExternalLink } from 'lucide-react';

interface ProtectedEmailProps {
  user: string;
  domain: string;
  label?: string;
  className?: string;
  showIcon?: boolean;
  directActionText?: string;
}

/**
 * ProtectedEmail Component
 * Prevents automated email harvester bots & crawlers from extracting emails.
 * Uses dynamic client-side assembly, obfuscated tokens, and event-driven mailto triggers.
 */
export function ProtectedEmail({ 
  user, 
  domain, 
  label, 
  className = "",
  showIcon = true,
  directActionText = "Contact Founder Support"
}: ProtectedEmailProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Dynamic reassembly upon user action only
  const getEmailAddress = () => `${user}@${domain}`;

  const handleDirectLaunch = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = ['mail', 'to:', user, '@', domain, '?subject=AzuraOS%20Founder%20Inquiry'].join('');
    window.location.href = target;
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(getEmailAddress());
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (!isRevealed) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          type="button"
          onClick={handleDirectLaunch}
          className="px-3.5 py-2 bg-gradient-to-r from-[#14B8A6]/20 to-[#0D9488]/20 hover:from-[#14B8A6]/30 hover:to-[#0D9488]/30 border border-[#14B8A6]/40 text-[#14B8A6] hover:text-white text-xs font-semibold rounded-xl transition flex items-center justify-center gap-2 cursor-pointer shadow-sm group"
          title="Direct click-to-contact (Protected from spam bots)"
        >
          <Mail size={14} className="text-[#14B8A6] group-hover:scale-110 transition-transform" />
          <span>{directActionText}</span>
          <ExternalLink size={12} className="opacity-70 group-hover:opacity-100" />
        </button>

        <button
          type="button"
          onClick={() => setIsRevealed(true)}
          className="p-2 bg-[#181E20] hover:bg-[#232B2E] border border-[#262F32] text-gray-400 hover:text-white rounded-xl transition cursor-pointer text-xs flex items-center gap-1"
          title="Reveal email address (Bot protected)"
        >
          <Shield size={13} className="text-gray-400" />
          <Eye size={12} />
        </button>
      </div>
    );
  }

  const emailAddress = getEmailAddress();

  return (
    <div className={`inline-flex flex-wrap items-center gap-2.5 bg-[#141A1C] border border-[#14B8A6]/50 rounded-2xl px-3.5 py-2 text-xs font-mono text-[#14B8A6] shadow-md animate-fade-in ${className}`}>
      {showIcon && <Mail size={14} className="text-[#14B8A6] shrink-0" />}
      <span className="select-all font-bold text-white tracking-wide">{emailAddress}</span>
      
      <div className="flex items-center gap-1.5 ml-auto">
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 hover:bg-[#20282B] text-gray-300 hover:text-white rounded-lg transition cursor-pointer"
          title="Copy email address"
        >
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
        </button>

        <button
          type="button"
          onClick={handleDirectLaunch}
          className="px-2.5 py-1 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-[11px] rounded-lg transition flex items-center gap-1 cursor-pointer shadow"
          title="Open email client"
        >
          <span>Email</span>
          <ExternalLink size={11} />
        </button>
      </div>
    </div>
  );
}
